// LabOrderForm.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import TableComponent from '../../../components/Table/TableComponent';
import { TableCell, TableRow } from '@mui/material';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import SearchBar from '../../../components/FormFilelds/PlanSearchBar';
import { apiHandler } from '../../../services/authService';


interface LabOrderFormProps {
  patientId: string;
}

interface LabOrder {
  id: number;
  labTestDate: string;
  labTestName: string;
  files: string;
  notes: string;
  link: string;
}

const LabOrderForm: React.FC<LabOrderFormProps> = ({ patientId }) => {
  const [searchTerm, setSearchTerm] = useState(''); // State for search input
  const [labOrders, setLabOrders] = useState<LabOrder[]>([]);
  const [page, setPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null); // State for the file content
  const [isFileOpen, setIsFileOpen] = useState(false); // State for file modal visibility

  const fetchLabOrders = async (search: string, page: number, limit: number) => {
    setLoading(true);
    setError(null);
  
    try {

      const url        = `${BASE_URL}/resource/docs?limit=${limit}&page=${page + 1}&searchkey=${search}&order_by=created_at&dir=2&slug=lab&user_id=${patientId}`;
      const response   = await apiHandler('', 'GET', {}, url);

      if (!response) {
          throw new Error("No response received from the server");
      }

      // const axiosConfig = getAxiosConfig(); // Get the full Axios configuration
      // const response = await axios.get(
      //   `${BASE_URL}/resource/docs?limit=${limit}&page=${page + 1}&searchkey=${search}&order_by=created_at&dir=2&slug=lab&user_id=${patientId}`,
      //   {
      //     headers: axiosConfig.headers, // Pass headers inside an object
      //   }
      // );
      const data = response.data.data.docs;
      const total = response.data.data.pagination.total;
  
      const orders = data.map((item: any) => ({
        id: item.id,
        labTestDate: item.addition_info.date,
        labTestName: item.addition_info.title,
        files: item.properties ? item.properties.file_path : null,
        notes: item.addition_info.notes || 'No notes',
        link: item.addition_info.file_url || '#',
      }));
  
      setLabOrders(orders);
      setTotalRecords(total); // Set the total record count for pagination
    } catch (err) {
      setError('Failed to fetch lab orders');
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    fetchLabOrders('', page, rowsPerPage); // Fetch records initially
  }, [page, rowsPerPage, patientId]); // Fetch data when page, rowsPerPage, or patientId changes

  const handleSearch = () => {
    setPage(0); // Reset to the first page for a new search
    fetchLabOrders(searchTerm, 0, rowsPerPage); // Call the API with the search term
  };

  const handleClearSearch = () => {
    setSearchTerm(''); // Clear the search input
    setPage(0); // Reset to the first page
    fetchLabOrders('', 0, rowsPerPage); // Fetch all lab orders without filtering
  };

  const handleFileClick = async (filePath: string | null) => {
    if (!filePath) {
      setError('No file available');
      return;
    }

    try {
      const url        = `${BASE_URL}/get-document-url?path=${filePath}`;
      const response   = await apiHandler('', 'GET', {}, url);

      if (!response) {
          throw new Error("No response received from the server");
      }

      // const axiosConfig = getAxiosConfig();
      // const response = await axios.get(
      //   `${BASE_URL}/get-document-url?path=${filePath}`,
      //   { headers: axiosConfig.headers }
      // );
      
      const fileUrl = response.data.data.url;

      setFileContent(fileUrl);
      setIsFileOpen(true); // Open the file modal
    } catch (err) {
      setError('Failed to fetch the file');
    }
  };

  const renderRow = (item: LabOrder, index: number) => {
    const fileExtension = item.files ? item.files.split('.').pop() : null;

    return (
      <TableRow key={item.id}>
        <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
        <TableCell>{item.labTestDate}</TableCell>
        <TableCell>{item.labTestName}</TableCell>
        <TableCell>
          {item.files ? (
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => handleFileClick(item.files)}
            >
              {fileExtension ? fileExtension : 'File'}
            </button>
          ) : (
            'No file available'
          )}
        </TableCell>
        <TableCell>{item.notes}</TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />

      {loading && <div>Loading...</div>}
      {error && <div className="text-red-500">{error}</div>}

      <TableComponent
        headers={['NO', 'Lab Test Date', 'Lab Test Name', 'Files', 'Notes']}
        data={labOrders}
        renderRow={renderRow}
      />

      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0); // Reset to the first page
        }}
      />

      {/* Modal or iframe for displaying file content */}
      {isFileOpen && fileContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-4 w-4/5 h-4/5">
            <button
              onClick={() => setIsFileOpen(false)} // Close the modal
              className="text-red-500 font-bold"
            >
              Close
            </button>
            <iframe src={fileContent} className="w-full h-full" />
          </div>
        </div>
      )}
    </div>
  );
};

export default LabOrderForm;
